/**
 * DAG 攻防树 → 视图模型（纯函数，可独立单测；不依赖 React Flow / DOM）。
 *
 * - 按 `side` 生成节点配色（AFF = team-aff 青蓝，NEG = team-neg 玫瑰红）；
 * - `stageRange` 包含当前环节的节点标记为 `active`（高亮）；
 * - 缺少预置 `position` 的题库数据自动退化为「双方分列」的确定性布局。
 */
import type { DagEdgeKind, DagNodeKind, DebateDag, Side } from '@zongheng/core-schema';

export interface DagNodeModel {
  id: string;
  side: Side;
  kind: DagNodeKind;
  label: string;
  summary: string;
  stageRange: [number, number];
  evidenceIds: string[];
  position: { x: number; y: number };
  /** stageRange 是否包含当前环节 */
  active: boolean;
}

export interface DagEdgeModel {
  id: string;
  source: string;
  target: string;
  kind: DagEdgeKind;
  label?: string;
  /** 两端节点都在当前环节激活时为 true */
  active: boolean;
}

export interface DagModel {
  nodes: DagNodeModel[];
  edges: DagEdgeModel[];
  activeNodeIds: string[];
  hasPositions: boolean;
}

export const DAG_KIND_LABEL: Record<DagNodeKind, string> = {
  PREMISE: '前提',
  EVIDENCE: '论据',
  ATTACK: '攻击',
  COUNTER: '反制',
  WEIGH: '权衡',
};

export const DAG_EDGE_LABEL: Record<DagEdgeKind, string> = {
  SUPPORTS: '支撑',
  ATTACKS: '攻击',
  BRANCHES: '分叉',
};

const COLUMN_GAP = 260;
const ROW_GAP = 110;
const MARGIN_X = 48;
const MARGIN_Y = 40;

function fallbackPositions(dag: DebateDag): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  const indexBySide: Record<Side, number> = { AFF: 0, NEG: 0 };
  for (const node of dag.nodes) {
    const index = indexBySide[node.side];
    indexBySide[node.side] += 1;
    positions.set(node.id, {
      x: MARGIN_X + (node.side === 'NEG' ? COLUMN_GAP : 0),
      y: MARGIN_Y + index * ROW_GAP,
    });
  }
  return positions;
}

/** 构建 DAG 视图模型；`currentStageIndex` 为 0 表示比赛未开始（无激活节点） */
export function buildDagModel(dag: DebateDag, currentStageIndex: number): DagModel {
  const hasPositions = dag.nodes.every((node) => Boolean(node.position));
  const fallback = hasPositions ? new Map<string, { x: number; y: number }>() : fallbackPositions(dag);

  const nodes: DagNodeModel[] = dag.nodes.map((node) => {
    const active =
      currentStageIndex > 0 &&
      currentStageIndex >= node.stageRange[0] &&
      currentStageIndex <= node.stageRange[1];
    return {
      id: node.id,
      side: node.side,
      kind: node.kind,
      label: node.label,
      summary: node.summary,
      stageRange: node.stageRange,
      evidenceIds: node.evidenceIds,
      position: node.position ?? fallback.get(node.id) ?? { x: 0, y: 0 },
      active,
    };
  });

  const activeNodeIds = nodes.filter((node) => node.active).map((node) => node.id);
  const activeSet = new Set(activeNodeIds);
  const edges: DagEdgeModel[] = dag.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    kind: edge.kind,
    label: edge.label,
    active: activeSet.has(edge.source) && activeSet.has(edge.target),
  }));

  return { nodes, edges, activeNodeIds, hasPositions };
}

/** 节点详情：摘要 + 引用论据（供点击节点后的摘要面板使用） */
export function describeDagNode(
  node: DagNodeModel,
  evidenceLibrary: { id: string; shortTitle: string; exactQuoteOrStats: string }[],
): { node: DagNodeModel; evidence: { id: string; shortTitle: string; exactQuoteOrStats: string }[] } {
  const evidenceById = new Map(evidenceLibrary.map((item) => [item.id, item]));
  const evidence = node.evidenceIds
    .map((id) => evidenceById.get(id))
    .filter((item): item is { id: string; shortTitle: string; exactQuoteOrStats: string } =>
      Boolean(item),
    );
  return { node, evidence };
}
